import { useState } from "react";
import { Outlet, NavLink, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { LayoutDashboard, Users, UtensilsCrossed, Dumbbell, CalendarDays, CalendarCheck, MessageSquare, CreditCard, TrendingUp, Camera, Menu, LogOut, Copy, Crown, Package, Gift, Rocket, Check, Sparkles, LifeBuoy, UserCircle, Download, Video, Shield, UserCheck, FileStack, ClipboardList, Pill, BookOpen, Activity, Flame, Settings, Globe, ShoppingCart, Droplets, Award, Sun } from "lucide-react";
import NotificationBell from "@/components/notifications/NotificationBell";
import { PwaInstallGuide } from "@/components/PwaInstallGuide";
import PushOptIn from "@/components/PushOptIn";
import { isMobile, isStandalone } from "@/lib/deviceDetect";
import { toast } from "sonner";
import { getSavedAccountsCount } from "@/lib/authAccounts";
import AuthLoadingScreen from "@/components/AuthLoadingScreen";
import { AccountSwitcherSection } from "@/components/AccountSwitcher";
import { hasStoredSession } from "@/lib/authStorage";

const coachNavGroups = [
  {
    title: "Accueil",
    items: [
      { path: "/coach/dashboard", label: "Tableau de bord", icon: LayoutDashboard },
      { path: "/coach/badges", label: "Mes badges", icon: Award },
    ],
  },
  {
    title: "Élèves & suivi",
    items: [
      { path: "/coach/students", label: "Mes élèves", icon: Users },
      { path: "/coach/daily-checkins", label: "Check-ins quotidiens", icon: Flame },
      { path: "/coach/meal-logs", label: "Journaux repas", icon: ClipboardList },
    ],
  },
  {
    title: "Nutrition",
    items: [
      { path: "/coach/diet-plans", label: "Plans alimentaires", icon: UtensilsCrossed },
      { path: "/coach/supplements", label: "Compléments", icon: Pill },
      { path: "/coach/plan-templates", label: "Modèles de plans", icon: FileStack },
    ],
  },
  {
    title: "Entraînement",
    items: [
      { path: "/coach/training-plans", label: "Programmes", icon: Dumbbell },
      { path: "/coach/exercise-library", label: "Bibliothèque", icon: BookOpen },
      { path: "/coach/exercise-videos", label: "Vidéos", icon: Video },
    ],
  },
  {
    title: "Medical",
    items: [
      { path: "/coach/blood-tests", label: "Bilan sanguin", icon: Droplets },
    ],
  },
  {
    title: "Planning & échanges",
    items: [
      { path: "/coach/calendar", label: "Calendrier", icon: CalendarDays },
      { path: "/coach/messages", label: "Messages", icon: MessageSquare },
    ],
  },
  {
    title: "Activité professionnelle",
    items: [
      { path: "/coach/packages", label: "Forfaits & tarifs", icon: Package },
      { path: "/coach/payments", label: "Paiements", icon: CreditCard },
      { path: "/coach/subscription", label: "Abonnement", icon: Crown },
      { path: "/coach/profile", label: "Profil public", icon: UserCircle },
    ],
  },
  {
    title: "Mon compte",
    items: [
      { path: "/coach/account", label: "Mon compte", icon: Settings },
      { path: "/coach/security", label: "Sécurité", icon: Shield },
      { path: "/coach/support", label: "Support", icon: LifeBuoy },
    ],
  },
];

const studentNavGroups = [
  {
    title: "Accueil",
    items: [
      { path: "/student/dashboard", label: "Tableau de bord", icon: LayoutDashboard },
      { path: "/student/today", label: "Session du jour", icon: Sun },
    ],
  },
  {
    title: "Mon accompagnement",
    items: [
      { path: "/student/my-coach", label: "Mon coach", icon: UserCheck },
      { path: "/student/sessions", label: "Séances", icon: CalendarCheck },
      { path: "/student/messages", label: "Messages", icon: MessageSquare },
    ],
  },
  {
    title: "Nutrition",
    items: [
      { path: "/student/diet-plan", label: "Plan alimentaire", icon: UtensilsCrossed },
      { path: "/student/meal-log", label: "Journal repas", icon: ClipboardList },
      { path: "/student/supplements", label: "Compléments", icon: Pill },
      { path: "/student/shopping-list", label: "Liste de courses", icon: ShoppingCart },
    ],
  },
  {
    title: "Entraînement",
    items: [
      { path: "/student/training-plan", label: "Programme", icon: Dumbbell },
      { path: "/student/workout-log", label: "Suivi des séances", icon: Activity },
    ],
  },
  {
    title: "Progression",
    items: [
      { path: "/student/progress", label: "Mesures & stats", icon: TrendingUp },
      { path: "/student/photos", label: "Photos de suivi", icon: Camera },
      { path: "/student/badges", label: "Mes badges", icon: Award },
    ],
  },
  {
    title: "Medical",
    items: [
      { path: "/student/blood-tests", label: "Bilan sanguin", icon: Droplets },
    ],
  },
  {
    title: "Outils",
    items: [
      { path: "/student/ai-chat", label: "Assistant IA", icon: Sparkles },
    ],
  },
  {
    title: "Mon compte",
    items: [
      { path: "/student/payments", label: "Paiements", icon: CreditCard },
      { path: "/student/profile", label: "Mon profil", icon: UserCircle },
      { path: "/student/support", label: "Support", icon: LifeBuoy },
    ],
  },
];

function navTestId(label) {
  return `nav-${label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;
}

function SidebarNav({ groups, onClose }) {
  return (
    <nav className="flex-1 overflow-y-auto px-3 py-2">
      {groups.map((group, groupIndex) => (
        <div key={group.title} className={groupIndex > 0 ? "mt-5" : ""}>
          <p className="px-4 mb-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
            {group.title}
          </p>
          <div className="space-y-0.5">
            {group.items.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-blue-50 text-blue-600"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`
                }
                data-testid={navTestId(link.label)}
              >
                <link.icon className="w-5 h-5 shrink-0" />
                <span className="truncate">{link.label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

/**
 * Modal bloquant pour les coachs sans abonnement actif
 */
function SubscriptionGate({ trialAvailable, onNavigate }) {
  return (
    <div className="fixed inset-0 z-50 bg-white/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-fade-in">
        {/* Header gradient */}
        <div className={`px-8 pt-8 pb-6 text-center ${trialAvailable ? 'bg-gradient-to-br from-purple-50 to-purple-100/50' : 'bg-gradient-to-br from-blue-50 to-blue-100/50'}`}>
          <div className={`h-16 w-16 rounded-2xl mx-auto flex items-center justify-center mb-4 ${trialAvailable ? 'bg-purple-100' : 'bg-blue-100'}`}>
            {trialAvailable ? (
              <Gift className="w-8 h-8 text-purple-600" />
            ) : (
              <Crown className="w-8 h-8 text-blue-600" />
            )}
          </div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">
            {trialAvailable
              ? "Bienvenue sur Coach ! 🎉"
              : "Abonnement requis"}
          </h2>
          <p className="text-slate-500 mt-2 text-sm">
            {trialAvailable
              ? "Commencez votre aventure avec 14 jours d'essai gratuit. Aucune carte bancaire n'est requise immédiatement."
              : "Vous devez avoir un abonnement actif pour accéder à votre espace coach."}
          </p>
        </div>

        {/* Content */}
        <div className="px-8 py-6 space-y-4">
          {trialAvailable && (
            <>
              <div className="flex items-center gap-3 text-sm text-slate-600">
                <div className="h-7 w-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
                <span><strong>14 jours gratuits</strong> — testez toutes les fonctionnalités</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-600">
                <div className="h-7 w-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
                <span>Gérez vos élèves, plans et paiements</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-600">
                <div className="h-7 w-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
                <span>Annulez à tout moment pendant l'essai</span>
              </div>
            </>
          )}

          <Button
            onClick={onNavigate}
            className={`w-full rounded-full h-12 text-base font-semibold gap-2 mt-2 ${
              trialAvailable
                ? 'bg-purple-600 hover:bg-purple-700'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {trialAvailable ? (
              <>
                <Rocket className="w-5 h-5" /> Démarrer mon essai gratuit
              </>
            ) : (
              <>
                <Crown className="w-5 h-5" /> Voir les abonnements
              </>
            )}
          </Button>

          {trialAvailable && (
            <p className="text-xs text-slate-400 text-center">
              0€ pendant 14 jours, puis à partir de 29,99€/mois
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [pwaGuideOpen, setPwaGuideOpen] = useState(false);

  if (loading || (!user && hasStoredSession())) {
    return <AuthLoadingScreen />;
  }

  if (!user) return <Navigate to="/login" replace />;

  const isCoachPath = location.pathname.startsWith('/coach');
  const isStudentPath = location.pathname.startsWith('/student');
  if (isCoachPath && user.role !== 'coach') return <Navigate to="/student/dashboard" />;
  if (isStudentPath && user.role !== 'student') return <Navigate to="/coach/dashboard" />;

  const navGroups = user.role === "coach" ? coachNavGroups : studentNavGroups;
  const accountPath = user.role === "coach" ? "/coach/account" : "/student/profile";
  const publicProfilePath = user.role === "coach" ? "/coach/profile" : null;
  const securityPath = user.role === "coach" ? "/coach/security" : null;
  const supportPath = user.role === "coach" ? "/coach/support" : "/student/support";
  const isMessagesPage = location.pathname.endsWith("/messages");

  // Pages autorisées sans abonnement
  const subscriptionPages = ['/coach/subscription', '/coach/subscription/success', '/coach/stripe-connect/return'];
  const isOnSubscriptionPage = subscriptionPages.some(p => location.pathname.startsWith(p));

  // Afficher le gate si coach sans abonnement actif (info directement dans user via /api/auth/me)
  const showSubscriptionGate =
    user.role === 'coach' &&
    user.has_active_subscription === false &&
    !isOnSubscriptionPage;

  const hasMultipleAccounts = getSavedAccountsCount() > 1;

  const handleLogout = async ({ all = false } = {}) => {
    const result = await logout({ all });
    if (result?.switchedTo) {
      toast.success(`Connecté en tant que ${result.switchedTo.name}`);
      navigate(`/${result.switchedTo.role}/dashboard`, { replace: true });
      return;
    }
    navigate("/login", { replace: true });
  };

  const copyCoachCode = () => {
    if (user.coach_code) {
      navigator.clipboard.writeText(user.coach_code);
      toast.success("Code coach copié !");
    }
  };

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Modal bloquant si pas d'abonnement */}
      {showSubscriptionGate && (
        <SubscriptionGate
          trialAvailable={user.trial_available}
          onNavigate={() => navigate('/coach/subscription')}
        />
      )}
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-white border-r border-slate-100 flex-shrink-0 overflow-hidden">
        <div className="p-6 pb-4 shrink-0">
          <h1 className="font-heading text-2xl font-bold tracking-tight uppercase text-slate-900" data-testid="sidebar-brand">COACH.</h1>
          <p className="mt-1 text-[11px] font-medium uppercase tracking-wider text-slate-400">
            {user.role === "coach" ? "Espace coach" : "Espace élève"}
          </p>
          {user.role === 'coach' && user.coach_code && (
            <button onClick={copyCoachCode} className="mt-2 flex items-center gap-1.5 text-xs text-slate-400 hover:text-blue-600 transition-colors" data-testid="copy-coach-code-btn">
              <Copy className="w-3 h-3" /> {user.coach_code}
            </button>
          )}
        </div>
        <SidebarNav groups={navGroups} />
        <div className="p-4 border-t border-slate-100">
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9">
              <AvatarImage src={user.picture} />
              <AvatarFallback className="bg-blue-100 text-blue-600 text-sm font-semibold">{user.name?.[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user.name}</p>
              <p className="text-xs text-slate-400 capitalize">{user.role}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="left" className="w-72 p-0 flex flex-col">
          <div className="p-6 pb-4 shrink-0">
            <h1 className="font-heading text-2xl font-bold tracking-tight uppercase text-slate-900">COACH.</h1>
            <p className="mt-1 text-[11px] font-medium uppercase tracking-wider text-slate-400">
              {user.role === "coach" ? "Espace coach" : "Espace élève"}
            </p>
          </div>
          <SidebarNav groups={navGroups} onClose={() => setSheetOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 bg-white border-b border-slate-100 flex items-center justify-between px-6 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setSheetOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="w-5 h-5" />
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <NotificationBell userRole={user.role} />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                <div className="relative">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user.picture} />
                    <AvatarFallback className="bg-blue-100 text-blue-600 text-xs font-semibold">{user.name?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  {hasMultipleAccounts && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
                      {getSavedAccountsCount()}
                    </span>
                  )}
                </div>
                <span className="hidden sm:block text-sm font-medium">{user.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <AccountSwitcherSection
                onLogoutCurrent={() => handleLogout({ all: false })}
                onLogoutAll={hasMultipleAccounts ? () => handleLogout({ all: true }) : undefined}
              />
              <DropdownMenuItem onClick={() => navigate(accountPath)}>
                <Settings className="w-4 h-4 mr-2" />
                {user.role === "coach" ? "Mon compte" : "Mon profil"}
              </DropdownMenuItem>
              {publicProfilePath && (
                <DropdownMenuItem onClick={() => navigate(publicProfilePath)}>
                  <Globe className="w-4 h-4 mr-2" /> Profil public
                </DropdownMenuItem>
              )}
              {securityPath && (
                <DropdownMenuItem onClick={() => navigate(securityPath)}>
                  <Shield className="w-4 h-4 mr-2" /> Sécurité
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => navigate(supportPath)}>
                <LifeBuoy className="w-4 h-4 mr-2" /> Support
              </DropdownMenuItem>
              {isMobile() && !isStandalone() && (
                <DropdownMenuItem onClick={() => setPwaGuideOpen(true)}>
                  <Download className="w-4 h-4 mr-2" /> Installer l'app
                </DropdownMenuItem>
              )}
              {!hasMultipleAccounts && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleLogout()} className="text-red-600" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" /> Déconnexion
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        {/* Content */}
        <main className={`flex-1 p-4 md:p-8 ${isMessagesPage ? "overflow-hidden flex flex-col min-h-0" : "overflow-y-auto"}`}>
          <div className={`animate-fade-in ${isMessagesPage ? "flex flex-col flex-1 min-h-0" : ""}`}>
            {!isMessagesPage && <PushOptIn />}
            <Outlet />
          </div>
        </main>
      </div>
      <PwaInstallGuide open={pwaGuideOpen} onOpenChange={setPwaGuideOpen} />
    </div>
  );
}
